import { useState, useCallback, useEffect, useRef } from 'react';
import { getSummaryHotEventListV2 } from '@op/services';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { IDiscussionItem } from '../components/HotEventPanel/types';
import { useHotEventTrend } from './useHotEventTrend';
import moment from 'moment';
import { useStableValue } from '@opm/utils/stableKey';

interface UseHotEventListOptions {
  autoFetch?: boolean;
  pageSize?: number;
  /** 首次加载无数据且有 has_data_start_time 时的日期回退回调 */
  onDateFallback?: (dateRange: {
    start_time: string;
    end_time: string;
  }) => void;
}

interface PageInfo {
  page: number;
  total: number;
}

/**
 * Hot Event V2 列表数据 Hook
 * 使用 /summary/hot_event/list_v2 接口，支持滚动分页加载
 *
 * 首次加载时若接口返回空数据且包含 has_data_start_time，
 * 会自动触发 onDateFallback 回调跳转到有数据的日期范围（仅触发一次，防无限循环）。
 */
export const useHotEventList = (
  filter?: any,
  options?: UseHotEventListOptions,
) => {
  const { autoFetch = true, pageSize = 15, onDateFallback } = options || {};
  const [list, setList] = useState<IDiscussionItem[]>([]);
  const [pageInfo, setPageInfo] = useState<PageInfo>({ page: 1, total: 0 });
  const [loadingMore, setLoadingMore] = useState(false);
  const pageRef = useRef(1);
  const stableFilter = useStableValue(filter);

  // 无数据回退相关字段
  const [hasDataStartTime, setHasDataStartTime] = useState<string>('');
  const [hasDataEndTime, setHasDataEndTime] = useState<string>('');
  const [hintTag, setHintTag] = useState<string>('');

  /** 防止 fallback 回调死循环：filter 变化时重置，触发一次后锁定 */
  const fallbackTriggeredRef = useRef(false);

  const { loading, error, wrapperCancelRequest } = useCancelRequestHook(); // 不传 clear，避免每次请求前清空 list

  // ─── 趋势图数据 Hook（独立管理，按需拉取） ───
  const {
    trendMap,
    timelineStartTime,
    timelineEndTime,
    loading: trendLoading,
    fetchTrend,
    setTrendMap,
  } = useHotEventTrend(stableFilter);

  const hasMore = pageInfo.total > list.length;

  const fetchList = useCallback(
    (params: any, append = false) => {
      const page = append ? pageRef.current + 1 : 1;
      const fetchParams = {
        ...params,
        page,
        page_size: pageSize,
        top_n: pageSize,
      };

      if (append) setLoadingMore(true);

      return wrapperCancelRequest(getSummaryHotEventListV2, fetchParams)
        .then((res: any) => {
          const resData = res?.data || res || {};
          const dataList = (resData.list || []) as IDiscussionItem[];
          const total = resData.total || dataList.length;

          setList(prev => (append ? [...prev, ...dataList] : dataList));
          setPageInfo({ page, total });
          pageRef.current = page;

          // 只在首次加载（非追加）时保存回退信息
          if (!append) {
            setHasDataStartTime(resData.has_data_start_time || '');
            setHasDataEndTime(resData.has_data_end_time || '');
            setHintTag(resData.hint_tag || '');

            // ─── 首次加载无数据自动跳转：在数据层直接触发，绕过 React 渲染时序 ───
            // 条件：非追加、结果为空、后端返回了有数据的日期范围、且尚未触发过 fallback
            if (
              dataList.length === 0 &&
              resData.has_data_start_time &&
              !fallbackTriggeredRef.current &&
              onDateFallback
            ) {
              fallbackTriggeredRef.current = true;
              onDateFallback({
                start_time: moment(resData.has_data_start_time).format(
                  'YYYY-MM-DD',
                ),
                end_time: moment(resData.has_data_end_time).format(
                  'YYYY-MM-DD',
                ),
              });
            }
          }

          // ─── 拉取趋势图数据（首次和滚动加载都需要） ───
          if (dataList.length > 0) {
            const clusterIds = dataList
              .map((item: IDiscussionItem) => item.cluster_id)
              .filter(Boolean);
            if (clusterIds.length > 0) {
              fetchTrend(clusterIds);
            }
          }

          return res;
        })
        .catch(() => {})
        .finally(() => {
          setLoadingMore(false);
        });
    },
    [pageSize, onDateFallback, fetchTrend],
  );

  // 加载更多
  const loadMore = useCallback(
    (params: any) => {
      if (loadingMore || !hasMore) return Promise.resolve();
      return fetchList(params, true);
    },
    [fetchList, loadingMore, hasMore],
  );

  // 自动请求（首次/筛选/排序变化时重置 — 立即清空旧数据，进入全屏 loading）
  useEffect(() => {
    if (!stableFilter || !autoFetch) return;
    setList([]);
    setPageInfo({ page: 1, total: 0 });
    pageRef.current = 1;
    fetchList(stableFilter, false);
    setTrendMap({});
  }, [stableFilter, autoFetch]);

  return {
    list,
    loading,
    loadingMore,
    error,
    hasMore,
    hasDataStartTime,
    hasDataEndTime,
    hintTag,
    /** 趋势图数据: { [cluster_id]: ITrendPoint[] } */
    trendMap,
    /** API 返回的趋势时间范围开始 */
    timelineStartTime,
    /** API 返回的趋势时间范围结束 */
    timelineEndTime,
    /** 趋势图加载状态 */
    trendLoading,
    fetchList,
    loadMore,
  };
};
